import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type SortChangedEvent,
} from 'ag-grid-community';
import { trendzGridTheme } from '@components/grid/trendzGridTheme';
import '@components/grid/agGridTheme.css';
import { buildCropsColumns } from '../columns';
import {
  CROP_SORT_KEYS,
  type Crop,
  type CropSortKey,
  type SortDirection,
} from '../schema';
import type { CommonResponse } from '@/types/api';

ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
  data: CommonResponse<Crop> | undefined;
  onEdit: (row: Crop) => void;
  onSortChange: (sort: { sortBy?: CropSortKey; sortDir?: SortDirection }) => void;
}

const DEFAULT_COL_DEF = {
  resizable: true,
  sortable: false,
  filter: false,
  minWidth: 100,
  unSortIcon: true,
};

const SORT_KEY_SET = new Set<string>(CROP_SORT_KEYS);

function readSort(event: SortChangedEvent): {
  sortBy?: CropSortKey;
  sortDir?: SortDirection;
} {
  const state = event.api.getColumnState();
  const active = state.find(
    (c) => c.sort != null && c.colId && SORT_KEY_SET.has(c.colId),
  );
  if (!active || !active.sort || !active.colId) return {};
  return {
    sortBy: active.colId as CropSortKey,
    sortDir: active.sort as SortDirection,
  };
}

export function CropsGrid({ data, onEdit, onSortChange }: Props) {
  const columnDefs = useMemo(() => buildCropsColumns({ onEdit }), [onEdit]);

  return (
    <div className="h-full w-full">
      <AgGridReact<Crop>
        theme={trendzGridTheme}
        rowData={data?.rowData ?? []}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        animateRows
        getRowId={(p) => p.data.id}
        suppressCellFocus
        suppressMultiSort
        rowHeight={56}
        onSortChanged={(e) => onSortChange(readSort(e))}
      />
    </div>
  );
}
